<template>
  <v-card class="pa-4" title="Staff profile">
    <p class="mb-4">
      Receipt printer selection is saved for this browser tab's session only.
    </p>
    <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>
    <v-select
      v-model="printer"
      :items="printers"
      :item-title="(item) => item.printerName ?? item.printerMAC"
      item-value="printerMAC"
      label="Receipt printer"
      :loading="loading"
      :disabled="loading || !!error"
      clearable
    />
    <v-btn v-if="error" :disabled="loading" @click="load">Retry</v-btn>
  </v-card>
</template>

<script setup lang="ts">
import { onMounted, ref, shallowRef } from "vue";
import { useReceiptPrinter } from "@/composables/shop/useReceiptPrinter";
import { useNotificationStore } from "@/stores/notification";
import { emptySearchParams } from "@/composables/useTableHelper";
import { usePrinterService } from "../../../cbi-generator/frontend/composables/usePrinterFrontendService";
import { PrinterStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/PrinterStaffSearchParamsMeta";
import type { PrinterStaffSearchParams } from "../../../cbi-generator/generated/dto/PrinterStaffSearchParams";
import type { PrinterStaffSearchResult } from "../../../cbi-generator/generated/dto/PrinterStaffSearchResult";
import { SortOrder } from "../../../cbi-generator/generated/dto/SortOrder";

const printer = useReceiptPrinter();
const printers = shallowRef<PrinterStaffSearchResult[]>([]);
const loading = ref(false);
const error = ref("");
const notification = useNotificationStore();

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const params = {
      ...emptySearchParams<PrinterStaffSearchParams>(
        PrinterStaffSearchParamsMeta,
      ),
      sort: [{ fieldName: "printerMAC", order: SortOrder.ASC }],
    };
    const items: PrinterStaffSearchResult[] = [];
    for (let page = 1; ; page++) {
      const result = await usePrinterService().searchPrinterStaff(
        params,
        page,
        100,
      );
      items.push(...result.items);
      if (items.length >= result.totalCount) break;
      if (!result.items.length)
        throw new Error("Printers changed while loading. Retry.");
    }
    printers.value = items;
  } catch (cause) {
    error.value =
      cause instanceof Error ? cause.message : "Unable to load printers.";
    notification.addSnackbar(error.value, "error");
  } finally {
    loading.value = false;
  }
}
onMounted(load);
</script>
